import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../../data";
import { PublicLayout } from "../../components/shell/WebLayout";
import { WEBSITE_URL } from "../../app/nav";
import { kitLangVorab } from "./kit-model";
import { loadDeletionStatus, normalizeDeletionCode, type DeletionStatus } from "./datenloeschung-model";

/**
 * Öffentliche Statusseite zum Löschwunsch über Meta (0156): /datenloeschung?code=TK-…. Ohne Anmeldung; es wird nur der Stand
 * des Vorgangs gezeigt, keine Daten eines Kontos. Sprache nach ?l=en bzw. der Browsersprache, umschaltbar. Ohne Code erklärt
 * die Seite, wie man Instagram-Daten löschen lässt (wie die Seite der Website, die Meta als „Data Deletion Instructions URL“ kennt).
 */
export function Datenloeschung() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("code");
  const [lang, setLang] = useState<"de" | "en">(() => kitLangVorab(`?${params.toString()}`, typeof navigator !== "undefined" ? navigator.language : undefined));
  const [status, setStatus] = useState<DeletionStatus | null>(null);
  const [input, setInput] = useState(raw ?? "");
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const dateText = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(lang === "en" ? "en-GB" : "de-DE", { dateStyle: "long", timeStyle: "short", timeZone: "UTC" }) + " UTC" : "–");

  useEffect(() => {
    if (!raw) {
      setStatus(null);
      return;
    }
    let alive = true;
    void loadDeletionStatus(api, raw).then((s) => alive && setStatus(s));
    return () => {
      alive = false;
    };
  }, [raw]);

  const guide = lang === "en" ? `${WEBSITE_URL}/en/instagram-data-deletion.html` : `${WEBSITE_URL}/instagram-daten-loeschen.html`;
  return (
    <PublicLayout lang={lang} themeLabel={t("Hell / Dunkel", "Light / dark")}>
      <title>{t("Instagram-Daten löschen · CreatorOffice", "Instagram data deletion · CreatorOffice")}</title>
      <main id="main" className="auth-shell stack">
        <section className="centerform panel stack">
          <div className="row between">
            <div className="eyebrow">{t("Datenlöschung", "Data deletion")}</div>
            <button className="" onClick={() => setLang(lang === "en" ? "de" : "en")} lang={lang === "en" ? "de" : "en"}>{lang === "en" ? "Deutsch" : "English"}</button>
          </div>
          <h1>{t("Löschung deiner Instagram-Daten", "Deletion of your Instagram data")}</h1>
          {raw && !status && <p role="status">{t("Stand wird geladen …", "Loading status …")}</p>}
          {status?.state === "completed" && (
            <div className="stack" role="status">
              <p><strong>{t("Erledigt.", "Done.")}</strong> {t("Alle Instagram-Daten, die zu deinem Konto bei CreatorOffice lagen, sind gelöscht: Zugangsschlüssel, Profilangaben, Kennzahlen, Zielgruppe, Beiträge und Monatswerte. Freigaben an Marken sind beendet.",
                "All Instagram data stored for your account at CreatorOffice has been deleted: access key, profile details, metrics, audience, posts and monthly values. Shares with brands have ended.")}</p>
              <p>
                {t("Bestätigungscode", "Confirmation code")}: <code>{status.code}</code><br />
                {t("Eingegangen", "Received")}: {dateText(status.requested_at)}<br />
                {t("Abgeschlossen", "Completed")}: {dateText(status.completed_at)}
              </p>
            </div>
          )}
          {status?.state === "pending" && <p role="status">{t("Dein Löschwunsch ist eingegangen und wird gerade bearbeitet.", "Your deletion request has been received and is being processed.")} <code>{status.code}</code></p>}
          {status?.state === "unknown" && (
            <p role="alert">{t("Zu diesem Bestätigungscode gibt es keinen Vorgang. Vorgänge werden nach zwölf Monaten gelöscht. Bitte prüfe den Code aus der Bestätigung von Instagram.",
              "There is no request for this confirmation code. Requests are deleted after twelve months. Please check the code from the Instagram confirmation.")}</p>
          )}
          {status?.state === "invalid" && <p role="alert">{t("Das ist kein gültiger Bestätigungscode. Er beginnt mit TK- und hat zwölf Zeichen danach.", "This is not a valid confirmation code. It starts with TK- followed by twelve characters.")}</p>}
          {status?.state === "failed" && <p role="alert">{t("Der Stand ließ sich gerade nicht abrufen. Bitte versuche es gleich noch einmal.", "The status could not be retrieved right now. Please try again shortly.")}</p>}
          <form className="stack" onSubmit={(e) => {
            e.preventDefault();
            const code = normalizeDeletionCode(input);
            const next = new URLSearchParams(params);
            if (code) next.set("code", code); else next.set("code", input.trim().slice(0, 40));
            setParams(next, { replace: true });
          }}>
            <label>
              {t("Bestätigungscode", "Confirmation code")}
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="TK-…" autoComplete="off" spellCheck={false} maxLength={40} />
            </label>
            <div className="row"><button className="primary" type="submit">{t("Stand abfragen", "Check status")}</button></div>
          </form>
          <p>{t("Ohne Code: Trenne die Verbindung im Creator-Portal unter Instagram (alles wird sofort gelöscht) oder entferne CreatorOffice in Instagram unter Einstellungen · Apps und Websites.",
            "Without a code: disconnect in the creator portal under Instagram (everything is deleted immediately) or remove CreatorOffice in Instagram under Settings · Apps and websites.")}</p>
          <p><a href={guide}>{t("So lässt du deine Instagram-Daten löschen", "How to have your Instagram data deleted")}</a></p>
        </section>
      </main>
    </PublicLayout>
  );
}
